import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, ChevronDown, BookmarkCheck, Sparkles, Clock3, AlertTriangle, SquareCheck, Check, Trash2, Gavel, Info } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import ResultCard from "../components/ResultCard";
import ResultModal from "../components/ResultModal";
import StyledSelect from "../components/ui/StyledSelect";
import { isToday, isWithinLast24h } from "../lib/format";
// import { DateRangePicker } from "rsuite";
import DateRangePicker from "rsuite/DateRangePicker";
import { subDays, startOfMonth, endOfMonth, subMonths, format } from "date-fns";
// import "rsuite/dist/rsuite.min.css";
import "rsuite/DateRangePicker/styles/index.css";

const PRIORITY_OPTIONS = [
  { v: "all", label: "All priorities" },
  { v: "high", label: "High (≥ 70)" },
  { v: "medium", label: "Medium (≥ 45)" },
];

const SORTS = [
  { v: "score_desc", label: "Score, high to low" },
  { v: "score_asc", label: "Score, low to high" },
  { v: "newest", label: "Newest first" },
  { v: "oldest", label: "Oldest first" },
];

// P2: Read/Unread filter
const READ_OPTIONS = [
  { v: "all", label: "Read & unread" },
  { v: "unread", label: "Unread only" },
  { v: "read", label: "Read only" },
];

// Strategic recommendation filter (open / closed workflow)
const ACTION_OPTIONS = [
  { v: "all", label: "All results" },
  { v: "open", label: "Open" },
  { v: "closed", label: "Closed" },
];

const USE_CASE_TABS = [
  { v: "all", label: "All" },
];

// --- rsuite DateRangePicker wiring -----------------------------------

// Same presets that used to live in the custom popover, expressed as
// rsuite's `ranges` shortcut list.
function buildDateRanges() {
  const today = new Date();
  return [
    { label: "Today", value: [today, today] },
    { label: "Yesterday", value: [subDays(today, 1), subDays(today, 1)] },
    { label: "Last 7 Days", value: [subDays(today, 6), today] },
    { label: "Last 30 Days", value: [subDays(today, 29), today] },
    { label: "This Month", value: [startOfMonth(today), endOfMonth(today)] },
    {
      label: "Last Month",
      value: [startOfMonth(subMonths(today, 1)), endOfMonth(subMonths(today, 1))],
    },
  ];
}

// Given a selected [start, end] pair, figure out which preset key it
// matches (so filters.date_range keeps sending "today" / "yesterday" /
// etc. to the backend exactly like before) — falls back to "custom".
function deriveDateRangeKey(start, end) {
  const fmt = (d) => format(d, "yyyy-MM-dd");
  const today = new Date();
  const presets = {
    today: [today, today],
    yesterday: [subDays(today, 1), subDays(today, 1)],
    "7d": [subDays(today, 6), today],
    "30d": [subDays(today, 29), today],
    this_month: [startOfMonth(today), endOfMonth(today)],
    last_month: [startOfMonth(subMonths(today, 1)), endOfMonth(subMonths(today, 1))],
  };
  const s = fmt(start);
  const e = fmt(end);
  for (const [key, [ps, pe]] of Object.entries(presets)) {
    if (fmt(ps) === s && fmt(pe) === e) return key;
  }
  return "custom";
}

// -----------------------------------------------------------------------

function parseList(sp, key) {
  const v = sp.getAll(key);
  return v.length > 0 ? v : [];
}

function MultiSelect({ options, selected, onToggle, testid }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-[#E2E8F0] bg-white text-sm"
      >
        <span className={selected.length > 0 ? "text-[#0F1F3D]" : "text-[#94A3B8]"}>
          {selected.length === 0 ? "All" : `${selected.length} selected`}
        </span>
        <ChevronDown className="h-4 w-4 text-[#64748B]" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />

          <div className="absolute z-20 mt-1 w-full bg-white border border-[#E2E8F0] rounded-md shadow-lg p-1 max-h-64 overflow-auto">
            {options.map((o) => {
              const checked = selected.includes(o.v);

              return (
                <label
                  key={o.v}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F1F5F9] cursor-pointer text-sm"
                  data-testid={`${testid}-opt-${o.v}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(o.v)}
                    className="h-4 w-4 rounded border-[#CBD5E1]"
                  />
                  <span className="text-[#0F1F3D]">{o.label}</span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default function Feed({ initialTab }) {
  const { user } = useAuth();
  const isOrgMember = user?.role === "org_member";
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [last24Total, setLast24Total] = useState(0);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [active, setActive] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [bookmarkIds, setBookmarkIds] = useState(new Set());
  // P1.6: per-user read tracking
  const [readIds, setReadIds] = useState(new Set());
  const [closingTenders, setClosingTenders] = useState([]);
  const [closingFallbackCount, setClosingFallbackCount] = useState(null);

  // Helpers mirrored from Tenders.jsx to keep banner count exactly in sync
  // with the Tenders page deadline filter (≤7 days, not closed).
  function feedParseClosingDate(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return isNaN(d) ? null : d;
  }
  function feedDaysUntil(dateStr) {
    const d = feedParseClosingDate(dateStr);
    if (!d) return null;
    const diff = d.getTime() - Date.now();
    return diff <= 0 ? 0 : Math.ceil(diff / 86400000);
  }
  function feedIsClosed(dateStr) {
    const d = feedParseClosingDate(dateStr);
    return !!d && d.getTime() <= Date.now();
  }

  useEffect(() => {
    api.get("/tenders", { params: { include_closed: true } }).then((r) => {
      setClosingTenders(r.data?.tenders || []);
      setClosingFallbackCount(null);
    }).catch(() => {
      // fallback to closing-soon endpoint if tenders fetch fails
      api.get("/tenders/closing-soon").then((rr) => {
        setClosingTenders(rr.data?.tenders || []);
        const full = rr.data?.count;
        setClosingFallbackCount(
          typeof full === "number" ? full : (rr.data?.tenders || []).length
        );
      }).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tab, setTab] = useState(() => {
    if (initialTab) return initialTab;
    const t = searchParams.get("tab");
    if (t === "bookmarks") return "bookmarks";
    if (t === "actions") return "actions";
    return "all";
  });

  const [filters, setFilters] = useState({
    date_range: searchParams.get("date_range") || "all",
    start_date: searchParams.get("start_date") || "",
    end_date: searchParams.get("end_date") || "",
    project_ids: parseList(searchParams, "project_id"),
    priority: searchParams.get("priority") || "all",
    use_case_type: searchParams.get("use_case_type") || "all",
    min_score: parseInt(searchParams.get("min_score") || 0),
    max_score: parseInt(searchParams.get("max_score") || 100),
    action_required: searchParams.get("action_required") === "true",
    action_status: ["open", "closed"].includes(searchParams.get("action_status"))
      ? searchParams.get("action_status")
      : "all",
    read_status: searchParams.get("read_status") || "all",
    search: searchParams.get("search") || "",
    sort: searchParams.get("sort") || "newest",
  });

  const [showCrossProject, setShowCrossProject] = useState(true);
  const [page, setPage] = useState(1);

  const dateRanges = useMemo(() => buildDateRanges(), []);

  // Banner count follows the selected project(s): empty selection = all
  // projects. Hidden automatically when the count is zero (render guard).
  const closingCount = useMemo(() => {
    if (closingFallbackCount !== null && filters.project_ids.length === 0) {
      return closingFallbackCount;
    }
    const sel = filters.project_ids;
    return closingTenders.filter((t) => {
      if (sel.length > 0 && !sel.includes(t.project_id)) return false;
      const dleft = feedDaysUntil(t.closing_date);
      return !feedIsClosed(t.closing_date) && dleft !== null && dleft <= 7;
    }).length;
  }, [closingTenders, closingFallbackCount, filters.project_ids]);

  const dateRangeValue = useMemo(() => {
    if (!filters.start_date || !filters.end_date) return null;
    return [
      new Date(`${filters.start_date}T00:00:00`),
      new Date(`${filters.end_date}T00:00:00`),
    ];
  }, [filters.start_date, filters.end_date]);

  const handleDateRangeChange = (value) => {
    if (!value) {
      setFilters((f) => ({ ...f, date_range: "all", start_date: "", end_date: "" }));
      return;
    }
    const [start, end] = value;
    setFilters((f) => ({
      ...f,
      date_range: deriveDateRangeKey(start, end),
      start_date: format(start, "yyyy-MM-dd"),
      end_date: format(end, "yyyy-MM-dd"),
    }));
  };

  const handleDateRangeClean = () => {
    setFilters((f) => ({ ...f, date_range: "all", start_date: "", end_date: "" }));
  };

  useEffect(() => {
    api.get("/projects")
      .then((r) => setProjects(r.data.projects || []))
      .catch(() => setProjects([]))
      .finally(() => setProjectsLoaded(true));
  }, []);

  const refreshBookmarks = useCallback(async () => {
    try {
      const r = await api.get("/bookmarks/ids");
      setBookmarkIds(new Set(r.data.ids || []));
    } catch (err) {
      console.error("Bookmark ids load failed", err);
    }
  }, []);

  // P1.6: load the set of result_ids the current user has marked as read
  const refreshReadIds = useCallback(async () => {
    try {
      const r = await api.get("/results/read-ids");
      setReadIds(new Set(r.data.ids || []));
    } catch (err) {
      console.error("Read ids load failed", err);
    }
  }, []);

  useEffect(() => {
    refreshBookmarks();
    refreshReadIds();
  }, [refreshBookmarks, refreshReadIds]);

  const applyClientFilters = useCallback(
    (rows) => {
      const {
        min_score,
        max_score,
        action_required,
        search,
        sort,
        read_status,
        date_range,
        start_date,
        end_date,
      } = filters;

      const now = new Date();

      let out = rows.filter((r) => {
        if (r.relevance_score < min_score) return false;
        if (r.relevance_score > max_score) return false;
        if (action_required && !r.action_required) return false;
        // Recommendation workflow filter. Missing action_status predates
        // the workflow and counts as open.
        const st = filters.action_status || "all";
        if (st === "open" && (!r.action_required || (r.action_status || "open") !== "open")) return false;
        if (st === "closed" && (!r.action_required || r.action_status !== "closed")) return false;

        if (read_status === "read" && !readIds.has(r.id)) return false;
        if (read_status === "unread" && readIds.has(r.id)) return false;

        if (date_range !== "all") {
          const d = new Date(r.run_date);

          switch (date_range) {
            case "today":
              if (!isToday(r.run_date)) return false;
              break;

            case "yesterday": {
              const y = new Date();
              y.setDate(y.getDate() - 1);
              if (d.toDateString() !== y.toDateString()) return false;
              break;
            }

            // case "7d":
            //   if (now - d > 7 * 24 * 60 * 60 * 1000) return false;
            //   break;

            // case "30d":
            //   if (now - d > 30 * 24 * 60 * 60 * 1000) return false;
            //   break;

            case "custom": {
              if (!start_date && !end_date) break;

              const start = start_date ? new Date(`${start_date}T00:00:00`) : null;
              const end = end_date ? new Date(`${end_date}T23:59:59`) : null;

              if (start && d < start) return false;
              if (end && d > end) return false;

              break;
            }
          }
        }

        if (search) {
          const s = search.toLowerCase();
          if (
            !(r.title || "").toLowerCase().includes(s) &&
            !(r.summary || "").toLowerCase().includes(s)
          ) {
            return false;
          }
        }

        return true;
      });

      if (sort === "score_asc") {
        out.sort((a, b) =>
          a.relevance_score - b.relevance_score ||
          (b.run_date || "").localeCompare(a.run_date || "")
        );
      } else if (sort === "newest") {
        // Default feed view: "New today" (last 24 h) by relevance first,
        // then older by recency — mirrors the backend's merge order so the
        // relevance ranking of New today holds across pages.
        const fresh = out.filter((r) => isWithinLast24h(r.run_date));
        const older = out.filter((r) => !isWithinLast24h(r.run_date));
        fresh.sort((a, b) =>
          b.relevance_score - a.relevance_score ||
          (b.run_date || "").localeCompare(a.run_date || "")
        );
        older.sort((a, b) => (b.run_date || "").localeCompare(a.run_date || ""));
        out = [...fresh, ...older];
      } else if (sort === "oldest") {
        out.sort((a, b) => (a.run_date || "").localeCompare(b.run_date || ""));
      } else {
        out.sort((a, b) =>
          b.relevance_score - a.relevance_score ||
          (b.run_date || "").localeCompare(a.run_date || "")
        );
      }

      return out;
    },
    [filters, readIds]
  );


  // Builds the shared query params for /results (used by both the
  // data-fetching effect and the post-delete refetch below).
  const buildResultsParams = useCallback(() => {
    const params = new URLSearchParams();

    filters.project_ids.forEach((id) => params.append("project_id", id));
    if (filters.priority && filters.priority !== "all") params.set("priority", filters.priority);
    if (filters.use_case_type && filters.use_case_type !== "all") params.set("use_case_type", filters.use_case_type);
    if (filters.min_score) params.set("min_score", filters.min_score);
    if (filters.max_score !== 100) params.set("max_score", filters.max_score);
    if (filters.action_required || tab === "actions") params.set("action_required", "true");
    if (filters.action_status && filters.action_status !== "all") {
      params.set("action_required", "true");
      params.set("action_status", filters.action_status);
    }
    if (filters.search) params.set("search", filters.search);
    if (filters.sort && filters.sort !== "newest") params.set("sort", filters.sort);

    if (filters.date_range === "custom") {
      params.set("date_range", "custom");
      if (filters.start_date) params.set("start_date", filters.start_date);
      if (filters.end_date) params.set("end_date", filters.end_date);
    } else if (filters.date_range !== "all") {
      params.set("date_range", filters.date_range);
    }

    return params;
  }, [filters, tab]);

  // Re-fetches one page of /results from the server. Used both for the
  // normal page load, and silently after a delete so the page backfills
  // from the next page instead of just shrinking. If the page we asked
  // for no longer exists (e.g. we deleted the last items on the last
  // page), step back a page instead of showing a blank page.
  const fetchResultsPage = useCallback(
    async ({ silent = false, targetPage = page } = {}) => {
      if (tab === "bookmarks") return;
      if (!silent) setLoading(true);

      const params = buildResultsParams();
      params.set("limit", "6");
      params.set("skip", String((targetPage - 1) * 6));

      try {
        const r = await api.get(`/results?${params.toString()}`);
        const newTotal = r.data.total || 0;
        const newTotalPages = Math.max(1, Math.ceil(newTotal / 6));

        if (targetPage > newTotalPages) {
          // This page is now past the end — step back and let the page
          // effect below re-fetch the correct (now-last) page.
          setPage(newTotalPages);
          return;
        }

        const rows = r.data.items || [];
        setLast24Total(r.data.last_24_total || 0);
        setItems(applyClientFilters(rows));
        setTotal(newTotal);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [tab, page, buildResultsParams, applyClientFilters]
  );


  useEffect(() => {
    setLoading(true);

    const params = new URLSearchParams();

    filters.project_ids.forEach((id) => params.append("project_id", id));

    if (filters.priority && filters.priority !== "all") {
      params.set("priority", filters.priority);
    }

    if (filters.use_case_type && filters.use_case_type !== "all") {
      params.set("use_case_type", filters.use_case_type);
    }

    if (tab === "bookmarks") {
      api
        .get(`/bookmarks?${params.toString()}`)
        .then((r) => {
          const filtered = applyClientFilters(r.data.items || []);
          setItems(filtered);
          setTotal(filtered.length);
        })
        .catch(() => {
          setItems([]);
          setTotal(0);
        })
        .finally(() => setLoading(false));
    } else {
      fetchResultsPage({ silent: false, targetPage: page });
    }

    const urlParams = new URLSearchParams(params);

    if (tab !== "all") urlParams.set("tab", tab);

    if (filters.read_status !== "all") urlParams.set("read_status", filters.read_status);

    if (tab === "bookmarks") {
      if (filters.min_score) urlParams.set("min_score", filters.min_score);
      if (filters.max_score !== 100) urlParams.set("max_score", filters.max_score);
      if (filters.action_required) urlParams.set("action_required", "true");
      if (filters.search) urlParams.set("search", filters.search);
      if (filters.sort && filters.sort !== "newest") {
        urlParams.set("sort", filters.sort);
      }

      // if (filters.date_range && filters.date_range !== "all") {
      //   urlParams.set("date_range", filters.date_range);
      // }

      if (filters.date_range === "custom") {
        params.set("date_range", "custom");

        if (filters.start_date) {
          params.set("start_date", filters.start_date);
        }

        if (filters.end_date) {
          params.set("end_date", filters.end_date);
        }
      } else if (filters.date_range !== "all") {
        params.set("date_range", filters.date_range);
      }
    }

    setSearchParams(urlParams, { replace: true });
  }, [filters, tab, page, applyClientFilters, setSearchParams, readIds,fetchResultsPage]);

  const update = (k) => (v) => {
    setFilters((f) => ({ ...f, [k]: v }));
  };

  const toggleInArray = (k) => (v) => {
    setFilters((f) => {
      const arr = f[k] || [];

      return {
        ...f,
        [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v],
      };
    });
  };

  const clearAll = () => {
    setFilters({
      date_range: "all",
      start_date: "",
      end_date: "",
      project_ids: [],
      priority: "all",
      use_case_type: "all",
      min_score: 0,
      max_score: 100,
      action_required: false,
      action_status: "all",
      read_status: "all",
      search: "",
      sort: "newest",
    });
  };

  const activeFilterCount =
    (filters.date_range !== "all" ? 1 : 0) +
    filters.project_ids.length +
    (filters.priority !== "all" ? 1 : 0) +
    (filters.use_case_type !== "all" ? 1 : 0) +
    (filters.min_score > 0 ? 1 : 0) +
    (filters.max_score < 100 ? 1 : 0) +
    (filters.action_required ? 1 : 0) +
    (filters.read_status !== "all" ? 1 : 0) +
    (filters.search ? 1 : 0) +
    (filters.sort !== "newest" ? 1 : 0);

  const projectMap = useMemo(() => {
    const m = {};
    projects.forEach((p) => {
      m[p.id] = p;
    });
    return m;
  }, [projects]);

  const projectOptions = useMemo(
    () => projects.map((p) => ({ v: p.id, label: p.name })),
    [projects]
  );

  const onOpen = (item) => {
    setActive(item);
    setModalOpen(true);
    // P1.6: auto-mark as read when the user opens the modal (option c — passive)
    if (!readIds.has(item.id)) {
      setReadIds((prev) => {
        const next = new Set(prev);
        next.add(item.id);
        return next;
      });
      api.post(`/results/${item.id}/read`).catch(() => {
        // revert on failure
        setReadIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      });
    }
  };

  // P1.6: manual toggle (option c — also allow user to mark unread again)
  const toggleRead = useCallback(
    async (item) => {
      const isOn = readIds.has(item.id);
      // optimistic update
      setReadIds((prev) => {
        const next = new Set(prev);
        if (isOn) next.delete(item.id);
        else next.add(item.id);
        return next;
      });
      try {
        if (isOn) {
          await api.delete(`/results/${item.id}/read`);
        } else {
          await api.post(`/results/${item.id}/read`);
        }
      } catch (err) {
        // revert on failure
        setReadIds((prev) => {
          const next = new Set(prev);
          if (isOn) next.add(item.id);
          else next.delete(item.id);
          return next;
        });
        toast.error("Could not update read status");
      }
    },
    [readIds]
  );

  const onCloseModal = () => {
    setModalOpen(false);
    setActive(null);
  };

  const toggleBookmark = useCallback(
    async (item) => {
      const isOn = bookmarkIds.has(item.id);

      setBookmarkIds((prev) => {
        const next = new Set(prev);
        if (isOn) next.delete(item.id);
        else next.add(item.id);
        return next;
      });

      try {
        if (isOn) {
          await api.delete(`/bookmarks/${item.id}`);
          toast.success("Bookmark removed");

          if (tab === "bookmarks") {
            setItems((prev) => prev.filter((r) => r.id !== item.id));
            setTotal((t) => Math.max(0, t - 1));
          }
        } else {
          await api.post("/bookmarks", {
            result_id: item.id,
            project_id: item.project_id,
          });

          toast.success("Saved to bookmarks");
        }
      } catch (err) {
        setBookmarkIds((prev) => {
          const next = new Set(prev);
          if (isOn) next.add(item.id);
          else next.delete(item.id);
          return next;
        });

        toast.error(err?.response?.data?.detail || "Bookmark failed");
      }
    },
    [bookmarkIds, tab]
  );

  const onFeedback = async (item, val) => {
    try {
      await api.post(`/results/${item.id}/feedback`, { feedback: val });
      toast.success("Feedback recorded");
    } catch (err) {
      toast.error("Feedback failed");
    }
  };

  // Strategic-recommendation workflow: sync open/closed + note into the
  // list and the open modal. Toast is shown by the modal.
  const onActionStatusChange = useCallback((item, data) => {
    const patch = {
      action_status: data.action_status,
      action_closed_note: data.action_closed_note ?? null,
      action_closed_at: data.action_closed_at ?? null,
    };
    setItems((prev) => prev.map((r) => (r.id === item.id ? { ...r, ...patch } : r)));
    setActive((prev) => (prev && prev.id === item.id ? { ...prev, ...patch } : prev));
  }, []);

  // P6 — per-user soft delete (hide) + multi-select
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  // P7 — delete confirmation dialog state. pending = { kind: 'one'|'bulk', item, items }
  const [pendingDelete, setPendingDelete] = useState(null);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelected(new Set());
  }, []);

  const toggleSelect = useCallback((item) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  }, []);

  const selectAllVisible = () => {
    setSelected(new Set(items.map((it) => it.id)));
  };

  // Single delete — opens the confirm dialog
  const hideOne = useCallback((item) => {
    setPendingDelete({ kind: "one", item });
  }, []);

  // Bulk delete — opens the confirm dialog with current selection
  const askBulkHide = () => {
    if (selected.size === 0) return;
    const toDelete = items.filter((it) => selected.has(it.id));
    setPendingDelete({ kind: "bulk", items: toDelete });
  };

  // Performs the actual hide once confirmed
  const performHideOne = async (item) => {
    setItems((prev) => prev.filter((r) => r.id !== item.id));
    setTotal((t) => Math.max(0, t - 1));
    setSelected((prev) => {
      if (!prev.has(item.id)) return prev;
      const next = new Set(prev);
      next.delete(item.id);
      return next;
    });
    try {
      await api.post(`/results/${item.id}/hide`);
      if (tab !== "bookmarks") fetchResultsPage({ silent: true });
      toast.success("Removed from your feed", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.delete(`/results/${item.id}/hide`);
              if (tab !== "bookmarks") {
                fetchResultsPage({ silent: true });
              } else {
                setItems((prev) => [item, ...prev]);
                setTotal((t) => t + 1);
              }
              toast.success("Restored");
            } catch {
              toast.error("Could not restore");
            }
          },
        },
      });
    } catch (err) {
      setItems((prev) => [item, ...prev]);
      setTotal((t) => t + 1);
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const performBulkHide = async (toDelete) => {
    const ids = toDelete.map((it) => it.id);
    setItems((prev) => prev.filter((r) => !ids.includes(r.id)));
    setTotal((t) => Math.max(0, t - ids.length));
    setSelected(new Set());
    setSelectMode(false);

    try {
      const r = await api.post("/results/bulk-hide", { result_ids: ids });
      const n = r.data?.hidden ?? ids.length;
      if (tab !== "bookmarks") fetchResultsPage({ silent: true });
      toast.success(`${n} ${n === 1 ? "item" : "items"} removed from your feed`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await Promise.all(ids.map((id) => api.delete(`/results/${id}/hide`)));
              if (tab !== "bookmarks") {
                fetchResultsPage({ silent: true });
              } else {
                setItems((prev) => [...toDelete, ...prev]);
                setTotal((t) => t + toDelete.length);
              }
              toast.success("Restored");
            } catch {
              toast.error("Could not restore all");
            }
          },
        },
      });
    } catch (err) {
      setItems((prev) => [...toDelete, ...prev]);
      setTotal((t) => t + toDelete.length);
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    }
  };

  // Confirms the dialog
  const confirmDelete = () => {
    if (!pendingDelete) return;
    if (pendingDelete.kind === "one") performHideOne(pendingDelete.item);
    else performBulkHide(pendingDelete.items);
    setPendingDelete(null);
  };

  // Exit select mode when changing tabs
  useEffect(() => {
    exitSelectMode();
  }, [tab, exitSelectMode]);


  const PAGE_SIZE = 6;
  const API_PAGE_SIZE = 6;

  const sortedItems = useMemo(() => {
    const fresh = items.filter((it) => isWithinLast24h(it.run_date));
    const earlier = items.filter((it) => !isWithinLast24h(it.run_date));

    return [...fresh, ...earlier];
  }, [items]);

  const totalPages = Math.max(1, Math.ceil(total / API_PAGE_SIZE));

  const dateRangePickerRef = useRef(null);

  // close the picker on ANY scroll — capture:true catches scrolling
  // from nested scroll containers too, not just window
  useEffect(() => {
    const handleScroll = () => {
      dateRangePickerRef.current?.close();
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, []);

  const paginatedItems = sortedItems;

  useEffect(() => {
    setPage(1);
  }, [filters, tab]);

  const resultsRef = useRef(null);

  const changePage = (newPage) => {
    setPage(newPage);

    resultsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };


  return (
    <div className="p-8 lg:p-12" data-testid="feed-page" ref={resultsRef}>
      <div>
        <div className="type-eyebrow text-[#64748B]">Intelligence</div>
        <h1 className="type-h1 text-[#0F1F3D] mt-3">Intelligence feed</h1>

        {!(projectsLoaded && projects.length === 0) && (
          <div className="type-body text-[#475569] mt-3 flex items-center gap-3 flex-wrap">
            <span>
              <span className="font-mono-tab font-bold text-[#0F1F3D]">
                {total}
              </span>{" "}
              {tab === "bookmarks" ? "saved" : "results"} match your filters
            </span>

            {activeFilterCount > 0 && (
              <button
                onClick={clearAll}
                data-testid="feed-clear-filters"
                className="type-caption text-[#0F1F3D] hover:underline font-semibold"
              >
                Clear {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}
              </button>
            )}
          </div>
        )}
      </div>

      {projectsLoaded && projects.length === 0 && (
        <div
          className="mt-8 rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-10 text-center"
          data-testid="feed-no-projects"
        >
          <p className="text-[#64748B]">No projects yet.</p>
          {!isOrgMember && (
            <Link
              to="/app/projects/new"
              data-testid="feed-create-project"
              className="text-[#0F1F3D] font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20 rounded-sm"
            >
              Click Here to create your first project
            </Link>
          )}
        </div>
      )}

      {projectsLoaded && projects.length === 0 ? null : closingCount > 0 && (
        <Link
          to={filters.project_ids.length === 1
            ? `/app/tenders?deadline=7&project_id=${filters.project_ids[0]}`
            : "/app/tenders?deadline=7"}
          data-testid="feed-tender-callout"
          className="mt-6 flex items-center justify-between gap-3 rounded-xl border border-[#FBBF24]/50 bg-[#FFFBEB] px-4 py-3 hover:border-[#F59E0B] transition-colors"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="h-8 w-8 shrink-0 rounded-md bg-[#FEF3C7] text-[#92400E] flex items-center justify-center">
              <Gavel className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#0F1F3D] truncate">
                {closingCount} relevant tenders closing soon
              </div>
            </div>
          </div>
          <span className="type-caption font-semibold text-[#92400E] whitespace-nowrap">View tenders →</span>
        </Link>
      )}

      {!(projectsLoaded && projects.length === 0) && (
        <>
        <div className="mt-8 flex flex-wrap items-center gap-3">
        <div
          className="inline-flex bg-white border border-[#E2E8F0] rounded-md p-1"
          data-testid="feed-tab-toggle"
        >
          <button
            onClick={() => setTab("all")}
            data-testid="tab-all-results"
            className={`px-4 py-1.5 rounded type-body-sm font-medium transition-colors ${tab === "all"
              ? "bg-[#0F1F3D] text-white"
              : "text-[#475569] hover:text-[#0F1F3D]"
              }`}
          >
            All Results
          </button>

          <button
            onClick={() => setTab("bookmarks")}
            data-testid="tab-bookmarks"
            className={`px-4 py-1.5 rounded type-body-sm font-medium transition-colors inline-flex items-center gap-1.5 ${tab === "bookmarks"
              ? "bg-[#0F1F3D] text-white"
              : "text-[#475569] hover:text-[#0F1F3D]"
              }`}
          >
            <BookmarkCheck className="h-3.5 w-3.5" />
            Bookmarks
          </button>

          {/* <button
            onClick={() => setTab("actions")}
            data-testid="tab-actions"
            className={`px-4 py-1.5 rounded type-body-sm font-medium transition-colors inline-flex items-center gap-1.5 ${tab === "actions"
              ? "bg-[#0F1F3D] text-white"
              : "text-[#475569] hover:text-[#0F1F3D]"
              }`}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Action Items
          </button> */}
        </div>





        <div className="ml-auto flex items-center gap-2 flex-row-reverse">
          {!selectMode ? (
            <button
              onClick={() => setSelectMode(true)}
              data-testid="feed-select-mode-on"
              disabled={items.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E2E8F0] bg-white text-[#475569] hover:text-[#0F1F3D] hover:border-[#0F1F3D] text-sm font-medium transition-colors disabled:opacity-50"
            >
              <SquareCheck className="h-3.5 w-3.5" />
              Select
            </button>
          ) : (
            <>
              <button
                onClick={selectAllVisible}
                data-testid="feed-select-all"
                className="text-sm text-[#0F1F3D] hover:underline font-medium"
              >
                Select all
              </button>
              <button
                onClick={exitSelectMode}
                data-testid="feed-select-mode-off"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E2E8F0] bg-white text-[#475569] hover:text-[#0F1F3D] text-sm font-medium"
              >
                Cancel
              </button>
            </>
          )}

          <div>
            <div className="flex items-center justify-between">
              {filters.search && (
                <button
                  type="button"
                  onClick={() => update("search")("")}
                  className="text-xs font-medium text-[#0F1F3D] hover:underline"
                  data-testid="feed-search-clear"
                >
                  Clear
                </button>
              )}
            </div>

            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-[#94A3B8]" />

              <input
                value={filters.search}
                onChange={(e) => update("search")(e.target.value)}
                data-testid="feed-search-input"
                placeholder="Search Title or summary"
                className="w-full pl-9 pr-10 py-2 rounded-md border border-[#E2E8F0] type-body-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]"
              />

              {filters.search && (
                <button
                  type="button"
                  onClick={() => update("search")("")}
                  className="absolute right-3 top-2 text-[#94A3B8] hover:text-[#0F1F3D] text-sm font-bold"
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          </div>


        </div>
      </div>

      {selectMode && selected.size > 0 && (
        <div
          className="mt-6 sticky top-0 z-20 flex items-center gap-3 px-5 py-3 bg-[#0F1F3D] text-white rounded-lg shadow-lg"
          data-testid="feed-bulk-bar"
        >
          <Check className="h-4 w-4" />
          <span className="text-sm font-medium" data-testid="feed-bulk-count">
            {selected.size} selected
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 rounded-md text-sm text-white/80 hover:text-white hover:bg-white/10 font-medium"
            >
              Clear
            </button>
            <button
              onClick={askBulkHide}
              data-testid="feed-bulk-delete"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-[#EF4444] text-white text-sm font-semibold hover:bg-[#DC2626] transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete {selected.size}
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <aside
          className="lg:col-span-3 bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-6 lg:sticky lg:top-6 lg:self-start lg:h-auto"
          data-testid="feed-filters"
        >
          <div className="flex items-center justify-between ">
            <div>
              <h3 className="text-sm font-semibold text-[#0F1F3D]">Filters</h3>

            </div>

            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearAll}
                data-testid="feed-clear-all"
                className="text-xs font-semibold text-[#0F1F3D] hover:underline"
              >
                Reset all
              </button>
            )}
          </div>

          <div className="!mt-2">
            <div className="flex items-center justify-between">
              <label className="type-eyebrow text-[#64748B]">Date</label>
              {filters.date_range !== "all" && (
                <button
                  type="button"
                  onClick={handleDateRangeClean}
                  data-testid="feed-filter-date-clear"
                  className="text-xs font-semibold text-[#0F1F3D] hover:underline"
                >
                  All time
                </button>
              )}
            </div>
            <div className="mt-1 relative feed-date-picker">
              <DateRangePicker
                ref={dateRangePickerRef}
                value={dateRangeValue}
                onChange={handleDateRangeChange}
                onClean={handleDateRangeClean}
                ranges={dateRanges}
                placeholder="All time"
                format="dd MMM yyyy"
                character=" → "
                cleanable
                block
                placement="bottomStart"
                data-testid="feed-filter-date"
                showOneCalendar
              />
            </div>
          </div>

          <div className="!mt-2">
            <label className="type-eyebrow text-[#64748B]">Projects</label>
            <div className="mt-1">
              <MultiSelect
                options={projectOptions}
                selected={filters.project_ids}
                onToggle={toggleInArray("project_ids")}
                testid="feed-filter-projects"
              />
            </div>
          </div>

          <div className="!mt-2">
            <label className="type-eyebrow text-[#64748B]">Priority</label>
            <div className="mt-1">
              <StyledSelect
                options={PRIORITY_OPTIONS}
                value={filters.priority}
                onChange={update("priority")}
                testid="feed-filter-priority"
              />
            </div>
          </div>

          <div className="!mt-2">
            <label className="type-eyebrow text-[#64748B]">Strategic Recommendations</label>
            <div className="mt-1">
              <StyledSelect
                options={ACTION_OPTIONS}
                value={filters.action_status}
                onChange={(v) =>
                  setFilters((f) => ({
                    ...f,
                    action_status: v,
                    action_required: v !== "all" ? true : f.action_required,
                  }))
                }
                testid="feed-filter-recommended-action"
              />
            </div>
          </div>

          <div className="!mt-2">
            <label className="type-eyebrow text-[#64748B]">Read status</label>
            <div className="mt-1">
              <StyledSelect
                options={READ_OPTIONS}
                value={filters.read_status}
                onChange={update("read_status")}
                testid="feed-filter-read-status"
              />
            </div>
          </div>

          <div className="!mt-2">
            <label className="type-eyebrow text-[#64748B]">Score range</label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="100"
                value={filters.min_score}
                onChange={(e) =>
                  update("min_score")(parseInt(e.target.value || 0))
                }
                data-testid="feed-min-score"
                className="w-20 px-2 py-2 rounded-md border border-[#E2E8F0] type-body-sm font-mono-tab"
              />

              <span className="type-body-sm text-[#64748B]">to</span>

              <input
                type="number"
                min="0"
                max="100"
                value={filters.max_score}
                onChange={(e) =>
                  update("max_score")(parseInt(e.target.value || 100))
                }
                data-testid="feed-max-score"
                className="w-20 px-2 py-2 rounded-md border border-[#E2E8F0] type-body-sm font-mono-tab"
              />
            </div>
          </div>

          <div className="!mt-4 pt-3 border-t border-[#E2E8F0]">
            <label
              className="flex items-center gap-2 cursor-pointer select-none group"
              data-testid="feed-filter-cross-project"
            >
              <input
                type="checkbox"
                checked={showCrossProject}
                onChange={(e) => setShowCrossProject(e.target.checked)}
                className="h-4 w-4 rounded border-[#CBD5E1] text-[#0F1F3D] focus:ring-[#0F1F3D]"
              />
              <span className="type-body-sm text-[#0F1F3D] group-hover:text-[#172A4F]">
                Show cross-project relevance
              </span>
              <div className="relative group/cptip inline-flex">
                <Info className="h-3.5 w-3.5 text-[#94A3B8] hover:text-[#0F1F3D] cursor-help" />
                <div
                  className="
                    absolute bottom-full left-1/2 mb-2 -translate-x-1/2
                    rounded-lg bg-[#0F172A] px-2.5 py-1.5
                    text-[11px] font-medium text-white whitespace-nowrap
                    opacity-0 invisible translate-y-1
                    transition-all duration-150
                    group-hover/cptip:opacity-100
                    group-hover/cptip:visible
                    group-hover/cptip:translate-y-0
                    pointer-events-none z-50 shadow-md
                  "
                >
                  Highlight cross-project intelligence in feed cards
                  <div className="absolute left-1/2 top-full h-1.5 w-1.5 -translate-x-1/2 -translate-y-0.5 rotate-45 bg-[#0F172A]" />
                </div>
              </div>
            </label>
          </div>

          <div className="!mt-2">
            <label className="type-eyebrow text-[#64748B]">Sort by</label>
            <div className="mt-1">
              <StyledSelect
                options={SORTS}
                value={filters.sort}
                onChange={update("sort")}
                testid="feed-sort"
              />
            </div>
          </div>
        </aside>

        <section className="lg:col-span-9 space-y-3 min-w-0" >
          {loading && (
            <div
              className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center type-body text-[#64748B]"
              data-testid="feed-loading"
            >
              Loading results
            </div>
          )}

          {!loading && items.length === 0 && (
            <div
              className="bg-white border border-[#E2E8F0] rounded-lg p-16 text-center"
              data-testid="feed-empty"
            >
              <div className="type-h2 text-[#0F1F3D]">
                {tab === "bookmarks"
                  ? "No bookmarks match your filters"
                  : "No results match your filters"}
              </div>

              <div className="type-body text-[#64748B] mt-4 prose-measure mx-auto">
                {tab === "bookmarks"
                  ? "Save articles from the feed by tapping the bookmark icon in the top-right of any result card."
                  : "Try clearing some filters, lowering the score range, or switching to a different use case tab."}
              </div>

              {activeFilterCount > 0 && (
                <button
                  onClick={clearAll}
                  className="mt-6 px-4 py-2.5 rounded-md bg-[#0F1F3D] text-white type-body-sm font-medium hover:bg-[#172A4F]"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}

          {!loading && items.length > 0 && (() => {
            // Split into "New today" (captured in the last 24 h, ordered by
            // relevance) vs "Older". Uses the same rolling 24 h window as the
            // backend bucket and the badge count — not the calendar day.
            const newItems = paginatedItems.filter((it) => isWithinLast24h(it.run_date));
            const oldItems = paginatedItems.filter((it) => !isWithinLast24h(it.run_date));
            return (
              <>
                {newItems.length > 0 && (
                  <>
                    <div
                      data-testid="feed-section-new"
                      className="flex items-center gap-2 pt-1 pb-2"
                    >
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#ECFDF5] text-[#047857] type-eyebrow font-semibold">
                        <Sparkles className="h-3 w-3" />
                        New today · {last24Total}
                      </span>
                      <span className="type-caption text-[#94A3B8]">
                        Articles captured in the last 24 h
                      </span>
                    </div>
                    {newItems.map((it) => (
                      <ResultCard
                        key={it.id}
                        item={it}
                        project={projectMap[it.project_id]}
                        onOpen={onOpen}
                        isBookmarked={bookmarkIds.has(it.id)}
                        onToggleBookmark={toggleBookmark}
                        isRead={readIds.has(it.id)}
                        onHide={hideOne}
                        selectMode={selectMode}
                        isSelected={selected.has(it.id)}
                        onToggleSelect={toggleSelect}
                        showCrossProject={showCrossProject}
                      />
                    ))}
                  </>
                )}

                {oldItems.length > 0 && (
                  <>
                    <div
                      data-testid="feed-section-old"
                      className={`flex items-center gap-2 pb-2 ${newItems.length > 0 ? "pt-6" : "pt-1"}`}
                    >
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F1F5F9] text-[#475569] type-eyebrow font-semibold">
                        <Clock3 className="h-3 w-3" />
                        Older Results · {oldItems.length}
                      </span>
                    </div>
                    {oldItems.map((it) => (
                      <ResultCard
                        key={it.id}
                        item={it}
                        project={projectMap[it.project_id]}
                        onOpen={onOpen}
                        isBookmarked={bookmarkIds.has(it.id)}
                        onToggleBookmark={toggleBookmark}
                        isRead={readIds.has(it.id)}
                        onHide={hideOne}
                        selectMode={selectMode}
                        isSelected={selected.has(it.id)}
                        onToggleSelect={toggleSelect}
                        showCrossProject={showCrossProject}
                      />
                    ))}
                  </>
                )}
              </>
            );
          })()}

          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between gap-3 pt-5">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => changePage(Math.max(1, page - 1))}
                className="px-4 py-2 rounded-md border border-[#E2E8F0] text-sm font-medium text-[#475569] hover:text-[#0F1F3D] hover:border-[#0F1F3D] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="type-caption text-[#64748B]">
                Page <span className="font-semibold text-[#0F1F3D]">{page}</span> of{" "}
                <span className="font-semibold text-[#0F1F3D]">{totalPages}</span>
              </div>

              <button
                type="button"
                disabled={page === totalPages}
                onClick={() => changePage(Math.min(totalPages, page + 1))}
                className="px-4 py-2 rounded-md border border-[#E2E8F0] text-sm font-medium text-[#475569] hover:text-[#0F1F3D] hover:border-[#0F1F3D] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </section>
      </div>
        </>
      )}

      <ResultModal
        item={active}
        project={active ? projectMap[active.project_id] : null}
        open={modalOpen}
        onClose={onCloseModal}
        isBookmarked={active ? bookmarkIds.has(active.id) : false}
        onToggleBookmark={toggleBookmark}
        onFeedback={onFeedback}
        onActionStatusChange={onActionStatusChange}
        isRead={active ? readIds.has(active.id) : false}
        onToggleRead={toggleRead}
      />

      {/* P7 — Delete confirmation (single + bulk) */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
      >
        <AlertDialogContent data-testid="feed-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDelete?.kind === "bulk"
                ? `Delete ${pendingDelete.items.length} item${pendingDelete.items.length === 1 ? "" : "s"} from your feed?`
                : "Delete this item from your feed?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.kind === "one" && pendingDelete.item && (
                <span className="block mb-3 italic text-[#0F1F3D]">
                  &ldquo;{pendingDelete.item.title}&rdquo;
                </span>
              )}
              The item{pendingDelete?.kind === "bulk" ? "s" : ""} will be hidden from your feed only —
              they remain in records and won&apos;t be re-fetched by the pipeline.
              You can undo this from the toast for a few seconds.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="feed-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="feed-delete-confirm-btn"
              className="bg-[#EF4444] text-white hover:bg-[#DC2626]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}